import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await login({ email, password });
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 bg-foreground text-background relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
          <div className="w-7 h-7 rounded-sm bg-background text-foreground flex items-center justify-center font-black tracking-tighter">L</div>
          <span className="font-display font-semibold tracking-tightest">LogiGrowth<span className="text-signal-500">.</span>AI</span>
        </Link>
        <div className="relative z-10">
          <div className="label-eyebrow text-background/70 mb-3">AI Revenue Operating System</div>
          <h2 className="font-display font-light text-4xl tracking-tightest leading-[1.05]">
            Welcome back to your<br />
            <span className="font-black">autonomous revenue team.</span>
          </h2>
        </div>
        <div className="font-mono text-xs tracking-wider text-background/50">v0.1.0 · MVP</div>
      </div>

      <div className="flex-1 flex flex-col p-6 lg:p-12 bg-background">
        <div className="flex justify-end"><ThemeToggle /></div>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <div>
              <div className="label-eyebrow mb-2">Sign in</div>
              <h1 className="font-display font-semibold text-3xl tracking-tightest">Access your workspace</h1>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" placeholder="you@company.com" autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" placeholder="••••••••" autoComplete="current-password" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full rounded-sm">
              {loading ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              New here?{" "}
              <Link to="/signup" className="text-foreground font-medium hover:underline" data-testid="login-signup-link">
                Create a workspace
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
